import { type FC, useCallback } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { useUnit } from 'effector-react'
import { Controller, useForm } from 'react-hook-form'

import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import {
  type FormValues,
  schema,
} from '@/dialogs/EditTimezoneDialog/validation.ts'
import type { DialogProps } from '@/dialogs/types'
import { $fanTimezone } from '@/models/chattingUI/chats/chats.ts'
import { updateTimeZoneFx } from '@/models/chattingUI/fanInfo/timezones.ts'
import { timezoneOptions } from '@/modules/chatting/ui/chat-window/fan-info/consts.ts'
import { resolveTimezoneOption } from '@/modules/chatting/ui/chat-window/fan-info/utils.ts'
import { StaticSelect } from '@/modules/chatting/ui/components/static-select.tsx'

const FORM_ID = 'edit-local-time-form'

interface Props extends DialogProps {}

export const EditTimezoneDialog: FC<Props> = ({ isOpen, onClose }) => {
  const timezone = useUnit($fanTimezone)

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { timezone: timezone ?? undefined },
  })

  const onSubmit = useCallback(
    async (values: FormValues) => {
      await updateTimeZoneFx(values.timezone)
      onClose()
    },
    [onClose],
  )

  return (
    <Dialog
      show={isOpen}
      onClose={onClose}
    >
      <div className="space-y-6">
        <Dialog.Header>
          <Dialog.Title>Edit Fan Timezone</Dialog.Title>
        </Dialog.Header>
        <form
          id={FORM_ID}
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <label className="text-title mb-[6px] text-sm font-medium">
            Timezone
          </label>
          <Controller
            control={form.control}
            name="timezone"
            render={({ field }) => {
              const selectedOption = resolveTimezoneOption(field.value)

              return (
                <>
                  <div className="bg-secondary-50 text-title dark:bg-secondary-600 rounded-md border border-border-secondary px-3 py-2">
                    {selectedOption?.label || 'Select Timezone'}
                  </div>
                  <div className="my-6 border-b border-border-secondary" />
                  <StaticSelect
                    options={timezoneOptions}
                    value={selectedOption?.value || ''}
                    onChange={field.onChange}
                    label="Select Timezone"
                    skeletonClassName="h-[405px] w-full"
                  />
                </>
              )
            }}
          />
        </form>
        <Dialog.ClosableFooter onClose={onClose}>
          <Button
            type="submit"
            form={FORM_ID}
            isLoading={form.formState.isSubmitting}
            disabled={!form.formState.isDirty}
          >
            Save
          </Button>
        </Dialog.ClosableFooter>
      </div>
    </Dialog>
  )
}

EditTimezoneDialog.displayName = 'EditTimezoneDialog'
